import { useEffect } from 'react'

import { selectCanRedo, selectCanUndo, useActivityStore } from '@/store/activity-store'

/** `<input>` types that don't hold editable text -- native text undo is irrelevant there. */
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
])

/**
 * True when focus is somewhere the browser's own text undo should win:
 * a text-entry `<input>`, a `<textarea>`, a `<select>`, or contenteditable.
 */
export function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type)
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true
  // `closest` (rather than only `isContentEditable`) also covers elements
  // nested inside an editable region.
  return (
    target.isContentEditable ||
    target.closest('[contenteditable]:not([contenteditable="false"])') != null
  )
}

function isModalDialogOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') != null
}

/**
 * Wires Ctrl/Cmd+Z (undo) and Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y (redo) to the
 * store's global undo history. Register once, at the app shell.
 *
 * Ignored while focus is in a text field (so native text undo keeps
 * working) and while a modal dialog is open (an undo landing behind a
 * dialog would change the activity the dialog is previewing/editing).
 * Availability is read from the store at keypress time, so the listener
 * never goes stale and is attached exactly once.
 */
export function useUndoRedoShortcuts(): void {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return
      if (isTextEditingTarget(event.target) || isModalDialogOpen()) return

      const key = event.key.toLowerCase()
      const isRedoCombo = (key === 'z' && event.shiftKey) || (key === 'y' && !event.shiftKey)
      const isUndoCombo = key === 'z' && !event.shiftKey

      const state = useActivityStore.getState()
      if (isRedoCombo && selectCanRedo(state)) {
        event.preventDefault()
        state.redo()
      } else if (isUndoCombo && selectCanUndo(state)) {
        event.preventDefault()
        state.undo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}
